/**
 * ExpandableTableRow Component
 *
 * A generic, reusable table row component that supports expand/collapse functionality.
 * Can be used in any table where rows need to show additional detail content.
 *
 * Features:
 * - Expand/collapse toggle with arrow indicator
 * - Smooth transitions
 * - Customizable styling
 * - Spans full table width when expanded
 */

import { type CSSProperties, type ReactNode } from 'react';

export interface ExpandableTableRowProps {
  /** Whether the row is currently expanded */
  isExpanded: boolean;
  /** Callback when the expand/collapse arrow is clicked */
  onToggle: () => void;
  /** Main row content (table cells) */
  children: ReactNode;
  /** Content to show when expanded (will be wrapped in a full-width td) */
  expandedContent: ReactNode;
  /** Number of columns to span when expanded */
  colSpan: number;
  /** Custom style for the arrow cell */
  arrowCellStyle?: CSSProperties;
  /** Custom style for the expanded content row */
  expandedRowStyle?: CSSProperties;
  /** Custom style for the main data row */
  rowStyle?: CSSProperties;
}

/**
 * Generic expandable table row component.
 *
 * Usage:
 * ```tsx
 * <ExpandableTableRow
 *   isExpanded={expanded}
 *   onToggle={() => setExpanded(!expanded)}
 *   colSpan={5}
 *   expandedContent={<div>Details...</div>}
 * >
 *   <td>Cell 1</td>
 *   <td>Cell 2</td>
 * </ExpandableTableRow>
 * ```
 */
export function ExpandableTableRow({
  isExpanded,
  onToggle,
  children,
  expandedContent,
  colSpan,
  arrowCellStyle,
  expandedRowStyle,
  rowStyle,
}: ExpandableTableRowProps) {
  // Default styles
  const defaultArrowCellStyle: CSSProperties = {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#666B71',
    borderBottom: '1px solid #E1E2E6',
    cursor: 'pointer',
    userSelect: 'none',
    width: '40px',
    textAlign: 'center',
  };

  const arrowIconStyle: CSSProperties = {
    display: 'inline-block',
    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
    transition: 'transform 200ms ease-in-out',
  };

  const defaultExpandedRowStyle: CSSProperties = {
    backgroundColor: 'rgba(81, 12, 255, 0.05)',
  };

  // Merge custom styles with defaults
  const finalArrowCellStyle = { ...defaultArrowCellStyle, ...arrowCellStyle };
  const finalExpandedRowStyle = { ...defaultExpandedRowStyle, ...expandedRowStyle };

  return (
    <>
      {/* Main data row */}
      <tr style={rowStyle}>
        <td style={finalArrowCellStyle} onClick={onToggle}>
          <span style={arrowIconStyle}>▶</span>
        </td>
        {children}
      </tr>

      {/* Expanded content row */}
      {isExpanded && (
        <tr style={finalExpandedRowStyle}>
          <td colSpan={colSpan} style={{ padding: 0, border: 'none' }}>
            {expandedContent}
          </td>
        </tr>
      )}
    </>
  );
}
